import { useMemo } from 'react';
import { css } from '@emotion/react';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { FormRow, SwitchField, TextField } from '@frontend/design-system';
import { useGetLocationOperatoryProviderList } from '../../../../hooks/scheduler-v3/use-get-location-operatory-provider-list';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import {
  GetRuleExceptionFormFieldProps,
  RuleExceptionFormConfig,
  RuleExceptionFormValues,
} from '../hooks/use-create-edit-rule-exception-form';
import { ExceptionTargetForm } from './exception-target-form';
import { NonRecurrenceForm } from './non-recurrence-form';
import { RecurrenceForm } from './recurrence-form';

type Props = {
  locationId: string;
  getFieldProps: GetRuleExceptionFormFieldProps;
  values: RuleExceptionFormValues;
  handleTargetChange: (newTarget: RuleExceptionFormConfig['target']['value']) => void;
  rRule: RRule | undefined;
  handleRRuleChange: (rrule: RRule | undefined) => void;
  isPastOccurrence: boolean;
};

export const AddEditRuleExceptionsForm = ({
  locationId,
  getFieldProps,
  values,
  handleTargetChange,
  rRule,
  handleRRuleChange,
  isPastOccurrence,
}: Props) => {
  const { t } = useTranslation('schedule-settings');

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId,
    pageConfig: { page: 1, limit: 100 },
  });

  const serviceList = useMemo(() => {
    return (serviceTypesQuery.data?.appointmentTypeGroups || [])
      .flatMap((group) => group.bookableAppointmentTypes ?? [])
      .map((service) => ({ value: service.id, label: service.displayName }));
  }, [serviceTypesQuery.data?.appointmentTypeGroups]);

  const { providerList, operatoryList } = useGetLocationOperatoryProviderList({
    locationIds: locationId ? [locationId] : [],
  });

  const ruleRecurrenceFormProps = useRuleRecurrenceForm({
    locationId,
    values,
    getAvailabilityRuleFieldProps: getFieldProps,
    rRule,
    handleRRuleChange,
  });

  return (
    <fieldset disabled={isPastOccurrence} css={fieldsetStyles(isPastOccurrence)}>
      <FormRow>
        <TextField
          {...getFieldProps('name')}
          label={''}
          aria-label={t('Name of Exception')}
          placeholder={t('Name of Exception*')}
        />
      </FormRow>
      <FormRow>
        <SwitchField label={t('Repeat This Exception')} {...getFieldProps('isRecurring')} />
      </FormRow>
      {values.isRecurring ? (
        <RecurrenceForm locationId={locationId} formProps={ruleRecurrenceFormProps} />
      ) : (
        <NonRecurrenceForm getFieldProps={getFieldProps} />
      )}
      <ExceptionTargetForm
        providerList={providerList}
        operatoryList={operatoryList}
        serviceList={serviceList}
        getFieldProps={getFieldProps}
        handleTargetChange={handleTargetChange}
      />
    </fieldset>
  );
};

const fieldsetStyles = (isPastOccurrence: boolean) =>
  css({
    border: 0,
    padding: 0,
    margin: 0,
    minInlineSize: 0,
    opacity: isPastOccurrence ? 0.5 : 1,
  });
